import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { HttpError } from '@vagusx/clients';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

interface UseCancelAppointmentResult {
  isCancelling: boolean;
  cancel: (id: string, reason?: string) => Promise<boolean>;
}

/**
 * Cancel an appointment. The backend commits the cancel to the EHR live and falls
 * back to an async reconcile (ADR-092); this hook just fires the call, toasts, and
 * invalidates the appointment queries so the detail sheet re-renders with the new
 * status + EHR-sync badge. Mirrors {@link useBooking}.
 */
export function useCancelAppointment(): UseCancelAppointmentResult {
  const queryClient = useQueryClient();
  const [isCancelling, setIsCancelling] = useState(false);

  const cancel = async (id: string, reason?: string): Promise<boolean> => {
    setIsCancelling(true);
    try {
      await getAppointmentClient().cancel(
        id,
        reason?.trim() ? { reason: reason.trim() } : undefined
      );
      toast.success('Appointment cancelled');
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
      return true;
    } catch (err) {
      // Surface the server's specific reason (e.g. "Cannot cancel appointment with
      // status: Completed") rather than a generic message.
      toast.error(
        err instanceof HttpError && err.message
          ? err.message
          : 'Failed to cancel appointment. Please try again.'
      );
      return false;
    } finally {
      setIsCancelling(false);
    }
  };

  return { isCancelling, cancel };
}
